import React, { useState, useEffect } from "react";
import {
  Box,
  Snackbar,
  Typography,
  Card,
  useTheme,
  Container,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../Redux/Reducers/uiSlice";
import ReportsTable from "./ReportsTable";
import ReportsDialog from "./ReportsDialog";
import { useReportDataLoader } from "./useReportDataLoader";
import { FundCountCols, TopwaterTradeCols } from "../../component/Methods";
import { clearReportPopup } from "../../Redux/Reducers/reportsSlice";
import { selectReports, selectReportPopupInfo } from "../../Redux/Reducers/selectors";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import ReportDataDialog from "./ReportDataDialog";
const ReportsPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reports = useSelector(selectReports);
  const reportPopupInfo = useSelector(selectReportPopupInfo);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [reportDataDialogOpen, setReportDataDialogOpen] = useState(false);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [title, setTitle] = useState("");
  const [reportType, setReportType] = useState("");
  const [columns, setColumns] = useState(FundCountCols);
  const { loadReportData, handleDataEdit } = useReportDataLoader();
  const handleAdd = () => {
    setDialogOpen(true);
  };
  useEffect(() => {
    if (reportPopupInfo && reportPopupInfo.name) {
      setTitle(reportPopupInfo.name);
      setRecordForEdit(reportPopupInfo.generation);

      // Determine report type and set appropriate columns
      if (reportPopupInfo.name.toLowerCase().includes("fundcount")) {
        setReportType("fundcount");
        setColumns(FundCountCols);
      } else if (reportPopupInfo.name.toLowerCase().includes("charles")) {
        setReportType("charles");
        setColumns(FundCountCols);
      } else if (reportPopupInfo.name.toLowerCase().includes("goldman")) {
        setReportType("goldman");
        setColumns(FundCountCols);
      } else if (reportPopupInfo.name.toLowerCase().includes("topwater")) {
        setReportType("topwater");
        setColumns(TopwaterTradeCols);
      } else {
        setReportType("report");
        setColumns(FundCountCols);
      }

      if (reportPopupInfo.generation.stage == 0) {
        setReportDataDialogOpen(true);
      }
      dispatch(clearReportPopup());
    }
  }, [reportPopupInfo, dispatch]);


  const handleViewHistoricalReports = (id) => {
    if (!id) {
      console.warn("No report ID provided for navigation");
      return;
    }

    // Ensure id is a primitive value, not an object

    const navigationId = typeof id === "object" ? id.id || id.reportId : id;

    navigate("/historical-reports", { state: { id: navigationId } });
  };

  const handleGenerate = (id, name) => {
    setTitle(name);
    setRecordForEdit({ reportId: id });

    // Determine report type and set appropriate columns
    if (name.toLowerCase().includes("fund count")) {
      setReportType("fundcount");
      setColumns(FundCountCols);
    } else if (name.toLowerCase().includes("charles")) {
      setReportType("charles");
      setColumns(FundCountCols);
    } else if (name.toLowerCase().includes("goldman")) {
      setReportType("goldman");
      setColumns(FundCountCols);
    } else if (name.toLowerCase().includes("topwater")) {
      setReportType("topwater");
      setColumns(TopwaterTradeCols);
    } else {
      setReportType("report");
      setColumns(FundCountCols);
    }

    sendWebSocketMessage(
      JSON.stringify({
        action: "generate_report",
        data: { report: id },
      })
    );

    dispatch(addActiveRequests("generate_report"));
    dispatch(updateLoadingMsg(`Generating ${name} Report`));
    dispatch(updateLoading(true));
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleReportDataDialogClose = () => {
    setReportDataDialogOpen(false);
    setRecordForEdit(null);
    setTitle("");
    setReportType("");
    setColumns(FundCountCols);
  };

  const handleSave = (report) => {
    if (!report || Object.keys(report).length === 0) return;
    // TODO: Implement save functionality
    setSnackbar({ open: true, message: "Report saved!" });
    setDialogOpen(false);
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
        background: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
        minHeight: "100vh",
        py: 3,
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            EOD Reports
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            Manage and generate end-of-day reports
          </Typography>
        </Box>

        <Card
          sx={{
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <ReportsTable
            reports={reports}
            onAdd={handleAdd}
            onViewHistoricalReports={handleViewHistoricalReports}
            onGenerate={handleGenerate}
          />
        </Card>
      </Container>

      <ReportsDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
      />

      {/* Unified Report Data Dialog */}
      <ReportDataDialog
        open={reportDataDialogOpen}
        onClose={handleReportDataDialogClose}
        title={title}
        recordForEdit={recordForEdit}
        reportType={reportType}
        columns={columns}
        onDataLoad={(recordForEdit, setData, setTextContent, setShowLoader, setError) => 
          loadReportData(recordForEdit, setData, setTextContent, setShowLoader, setError, reportType)
        }
        onDataEdit={handleDataEdit}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default ReportsPage;
